/**
 * The five things that can cross in a trade, drawn the same way everywhere they appear.
 *
 * A trade is rendered in six places on this page — the submission form, both sides of an
 * awaiting card, the window strip's one-line summary, a counter's two columns, and the archive —
 * and in the pass before this one each of them formatted a pick slightly differently. So the
 * grammar is here once:
 *
 *   PLAYER   position chip · club chip · name        (chip-system.md, shared with Declare/Farm)
 *   PICK     monospaced, verbatim, plus an origin when a GM holds two in that round
 *   RIGHTS   the farm player, marked as rights rather than the player himself (2022-farm-system)
 *   ₣        green, the FAAB grammar the ledger already uses
 *   CLAUSE   amber, monospaced, and never converted into an asset anyone owns
 *
 * The clause case is the one worth defending. "Right to swap 6th rd picks in 2026 (John may
 * decline)" is a real line from the log, and every earlier attempt to model it turned it into a
 * pick that somebody owned — which is exactly what it is not. It is a sentence two men agreed to,
 * and it stays a sentence.
 */

import { PlayerClubChip, PositionChip } from './chips.js';
import { findPlayer } from '../domain/playerBank.js';
import { BUNDLED_BANK } from '../domain/playerBank.js';
import type { LiveTradeAsset } from '../store/types.js';

export const ASSET_LABEL: Readonly<Record<LiveTradeAsset['kind'], string>> = {
  player: 'Player', pick: 'Pick', rights: 'Farm rights', faab: 'FAAB', clause: 'Clause',
};

/**
 * The archive writes a player as "WR Justin Jefferson" — position and name in one string.
 *
 * That is how eleven years of the sheet has it, and it is not going to be rewritten. But the chip
 * grammar needs them apart, and `findPlayer` cannot match a name with a position glued to the
 * front of it: every player in the log came back unresolved, so 92 names drew with no club chip
 * at all, which read as "the league has no club data" rather than "the name has a prefix".
 *
 * So the prefix is stripped here, once, and both halves are handed on. Only the six real
 * positions count as a prefix — "Rights to ..." and a name that genuinely starts with a word in
 * capitals are left alone.
 */
const POS_PREFIX = /^(QB|RB|WR|TE|K|DEF|IDP)\s+(.+)$/;

export function splitPlayer(text: string): { pos: string | null; name: string } {
  const m = POS_PREFIX.exec(text.trim());
  return m ? { pos: m[1]!, name: m[2]! } : { pos: null, name: text };
}

/**
 * The position a player is drawn with.
 *
 * The player RECORD wins over the prefix in the log, because a man who changed position since
 * 2019 is at his current one on today's board and the sheet was written at the time. The prefix
 * is the fallback for somebody who has left the league's index entirely — most of the 2015 names.
 */
export function positionOf(name: string): string | null {
  const { pos, name: bare } = splitPlayer(name);
  return findPlayer(BUNDLED_BANK, bare)?.pos ?? pos;
}

/** The name without its position prefix — what the club lookup and the eye both want. */
export const bareName = (text: string): string => splitPlayer(text).name;

/**
 * One asset, inline — for a side of a card, where the assets stack and each is its own pill.
 *
 * `onRemove` turns it into a form row. Same component either way, because a trade being composed
 * and a trade being read should not look like two different objects.
 */
export function Asset(
  { a, onRemove, origin }:
  { a: LiveTradeAsset; onRemove?: () => void; origin?: React.ReactNode },
) {
  return (
    <span className={`trasset ${a.kind}`}>
      <span className="trkind">{ASSET_LABEL[a.kind]}</span>
      {a.kind === 'player' && (
        <>
          <PositionChip pos={a.position ?? positionOf(a.text)} />
          <PlayerClubChip player={bareName(a.text)} />
          <span className="trname">{bareName(a.text)}</span>
        </>
      )}
      {a.kind === 'pick' && <span className="trpick">{a.text}</span>}
      {a.kind === 'pick' && origin}
      {a.kind === 'rights' && <span className="trname">{a.text}</span>}
      {a.kind === 'faab' && <span className="trmoney">{a.text}</span>}
      {a.kind === 'clause' && <span className="trclause">{a.text}</span>}
      {onRemove && (
        <button type="button" className="trx" onClick={onRemove}
          aria-label={`Remove ${a.text}`}>×</button>
      )}
    </span>
  );
}

/**
 * A player without the pill around him — for the "Matt gets" column, where the border would be
 * a box round a single name and the design draws him bare.
 */
export function AssetLine({ a }: { a: LiveTradeAsset }) {
  if (a.kind === 'player') {
    return (
      <span className="trline">
        <PositionChip pos={a.position ?? positionOf(a.text)} />
        <PlayerClubChip player={bareName(a.text)} />
        <span className="trname">{bareName(a.text)}</span>
      </span>
    );
  }
  return <Asset a={a} />;
}

/** "RB Bijan Robinson for a 2026 3rd and ₣12" — the window strip's one line. */
export function assetPhrase(assets: readonly LiveTradeAsset[]): string {
  const parts = assets.map((a) => {
    if (a.kind === 'player') {
      const pos = a.position ?? positionOf(a.text);
      return pos ? `${pos} ${bareName(a.text)}` : a.text;
    }
    if (a.kind === 'rights') return `rights to ${a.text}`;
    return a.text;
  });
  if (parts.length <= 1) return parts[0] ?? 'nothing';
  return `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
}
